import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import {
  CircleCheckIcons,
  ClipBoardIcons,
  ListCheckIcons,
  NewUserIcons,
  ShieldAlertIcons,
  StarIcons,
  UsersIcons,
} from '../../../../../icons/icons';
import { Applicant } from '../../../../../utils/types/JobOpenings';

export interface CandidateStatusLegendProps {
  candidates: Applicant[];
}

export interface StatusConfig {
  label: string;
  bgColor: string;
  borderColor: string;
  iconColor: string;
  showHeadshot: boolean;
  icon: React.ComponentType<{ className?: string }> | null;
}

export type StatusConfigMap = Record<string, StatusConfig>;

const STATUS_CONFIG: StatusConfigMap = {
  'Unsuccessful-Pending': {
    label: 'Unsuccessful (Pending)',
    bgColor: '#fff7ed',
    borderColor: '#f97316',
    iconColor: '#ea580c',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Unsuccessful-Closed': {
    label: 'Unsuccessful (Closed)',
    bgColor: '#fef2f2',
    borderColor: '#ef4444',
    iconColor: '#dc2626',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Shortlisted': {
    label: 'Shortlisted',
    bgColor: '#ecfdf5',
    borderColor: '#10b981',
    iconColor: '#059669',
    showHeadshot: false,
    icon: ListCheckIcons,
  },
  'Suitable': {
    label: 'Suitable',
    bgColor: '#eff6ff',
    borderColor: '#3b82f6',
    iconColor: '#2563eb',
    showHeadshot: false,
    icon: CircleCheckIcons,
  },
  'Outstanding': {
    label: 'Outstanding',
    bgColor: '#faf5ff',
    borderColor: '#a855f7',
    iconColor: '#9333ea',
    showHeadshot: false,
    icon: StarIcons,
  },
  'New': {
    label: 'New',
    bgColor: '#ecfeff',
    borderColor: '#06b6d4',
    iconColor: '#0891b2',
    showHeadshot: false,
    icon: NewUserIcons,
  },
  'Auto-Assessment': {
    label: 'Assessment',
    bgColor: '#fffbeb',
    borderColor: '#f59e0b',
    iconColor: '#d97706',
    showHeadshot: false,
    icon: ClipBoardIcons,
  },
  'Psychometric': {
    label: 'Psychometric',
    bgColor: '#f5f3ff',
    borderColor: '#8b5cf6',
    iconColor: '#7c3aed',
    showHeadshot: false,
    icon: ListCheckIcons,
  },
  'Wellness': {
    label: 'Wellness',
    bgColor: '#f0fdfa',
    borderColor: '#14b8a6',
    iconColor: '#0d9488',
    showHeadshot: false,
    icon: ListCheckIcons,
  },
  'CCA': {
    label: 'CCA',
    bgColor: '#f0f9ff',
    borderColor: '#0284c7',
    iconColor: '#0369a1',
    showHeadshot: false,
    icon: ClipBoardIcons,
  },
  'Interview': {
    label: 'Interview',
    bgColor: '#eef2ff',
    borderColor: '#6366f1',
    iconColor: '#4f46e5',
    showHeadshot: false,
    icon: UsersIcons,
  },
  'Offer-Extended': {
    label: 'Offer Extended',
    bgColor: '#eff6ff',
    borderColor: '#3b82f6',
    iconColor: '#2563eb',
    showHeadshot: false,
    icon: CircleCheckIcons,
  },
  'Offer-Accepted': {
    label: 'Offer Accepted',
    bgColor: '#ecfdf5',
    borderColor: '#10b981',
    iconColor: '#059669',
    showHeadshot: false,
    icon: CircleCheckIcons,
  },
  'Offer-Declined': {
    label: 'Offer Declined',
    bgColor: '#fff7ed',
    borderColor: '#f97316',
    iconColor: '#ea580c',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Offer-Withheld': {
    label: 'Offer Withheld',
    bgColor: '#fffbeb',
    borderColor: '#f59e0b',
    iconColor: '#d97706',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Waitlisted': {
    label: 'Waitlisted',
    bgColor: '#f8fafc',
    borderColor: '#64748b',
    iconColor: '#475569',
    showHeadshot: false,
    icon: ListCheckIcons,
  },
  'Not-Suitable': {
    label: 'Not Suitable',
    bgColor: '#fff1f2',
    borderColor: '#f43f5e',
    iconColor: '#e11d48',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Disqualified': {
    label: 'Disqualified',
    bgColor: '#fef2f2',
    borderColor: '#ef4444',
    iconColor: '#dc2626',
    showHeadshot: false,
    icon: ShieldAlertIcons,
  },
  'Success': {
    label: 'Success',
    bgColor: '#f0fdf4',
    borderColor: '#22c55e',
    iconColor: '#16a34a',
    showHeadshot: false,
    icon: CircleCheckIcons,
  },
};

const DEFAULT_STATUS_CONFIG: StatusConfig = {
  label: 'Unknown',
  bgColor: '#f8fafc',
  borderColor: '#94a3b8',
  iconColor: '#64748b',
  showHeadshot: false,
  icon: ShieldAlertIcons,
};

const getStatusConfig = (status: string): StatusConfig => {
  return (
    STATUS_CONFIG[status] || {
      ...DEFAULT_STATUS_CONFIG,
      label: status || 'Unknown',
    }
  );
};

const getStatusCounts = (candidates: Applicant[]): Record<string, number> => {
  const statusCounts: Record<string, number> = {};
  candidates.forEach((candidate) => {
    const status = candidate.applicantStatus || 'Unknown';
    statusCounts[status] = (statusCounts[status] || 0) + 1;
  });
  return statusCounts;
};

const STATUS_ORDER = [
  'New',
  'Suitable',
  'Outstanding',
  'Waitlisted',
  'Auto-Assessment',
  'Psychometric',
  'Wellness',
  'CCA',
  'Shortlisted',
  'Interview',
  'Offer-Extended',
  'Offer-Accepted',
  'Offer-Declined',
  'Offer-Withheld',
  'Success',
  'Unsuccessful-Pending',
  'Unsuccessful-Closed',
  'Not-Suitable',
  'Disqualified',
];

interface LegendItemProps {
  count: number;
  config: StatusConfig;
}

const LegendItem = ({ count, config }: LegendItemProps) => {
  const {
    label,
    bgColor,
    borderColor,
    iconColor,
    showHeadshot,
    icon: IconComponent,
  } = config;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        flexShrink: 0,
        borderRadius: '9999px',
        border: '1px solid',
        borderColor: 'grey.300',
        bgcolor: 'background.paper',
        px: 1,
        py: 0.5,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      }}
    >
      <Box
        sx={{
          width: 20,
          height: 20,
          bgcolor: bgColor,
          borderColor: borderColor,
          borderWidth: 2,
          borderStyle: 'solid',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          color: iconColor,
          '& svg': {
            width: 10,
            height: 10,
            strokeWidth: 2,
          },
        }}
      >
        {showHeadshot ? (
          <UsersIcons />
        ) : (
          IconComponent && <IconComponent />
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{
          whiteSpace: 'nowrap',
          fontSize: '11px',
          fontWeight: 600,
          color: 'text.primary',
        }}
      >
        {label}{' '}
        <span style={{ color: '#64748b', fontWeight: 500 }}>({count})</span>
      </Typography>
    </Stack>
  );
};

export const CandidateStatusLegend = ({
  candidates,
}: CandidateStatusLegendProps) => {
  const statusCounts = getStatusCounts(candidates);

  const sortedStatuses = STATUS_ORDER.filter(
    (status) => statusCounts[status] > 0
  ).concat(
    Object.keys(statusCounts).filter(
      (status) => !STATUS_ORDER.includes(status)
    )
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%)',
        p: 0.75,
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          height: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'rgba(0, 0, 0, 0.15)',
          borderRadius: '2px',
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 'max-content' }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            flexShrink: 0,
            borderRadius: '9999px',
            bgcolor: 'background.paper',
            px: 1.25,
            py: 0.5,
            border: '1px solid',
            borderColor: 'grey.300',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              bgcolor: 'primary.main',
              borderRadius: '50%',
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            Candidate States:
          </Typography>
        </Stack>

        {sortedStatuses.map((status) => (
          <LegendItem
            key={status}
            count={statusCounts[status]}
            config={getStatusConfig(status)}
          />
        ))}
      </Stack>
    </Paper>
  );
};
